import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// const getProductSynopsis = async (productId, type) => {
//     console.log(productId, type, 'getProductSynopsis')
//     const response = await axiosInstance.get(
//         `/products/public-product-synopsis/${productId}/${type}`
//     );
//     return response.data;
// };

// Helper function to attempt to complete broken JSON
const attemptToCompleteJson = jsonStr => {
  // Count opening and closing braces to check if JSON is complete
  const openBraces = (jsonStr.match(/{/g) || []).length;
  const closeBraces = (jsonStr.match(/}/g) || []).length;

  // Count opening and closing brackets for arrays
  const openBrackets = (jsonStr.match(/\[/g) || []).length;
  const closeBrackets = (jsonStr.match(/\]/g) || []).length;

  // Count opening and closing quotes (only count those not escaped)
  const quotes = (jsonStr.match(/(?<!\\)"/g) || []).length;
  let completedJson = jsonStr;

  // If we have an odd number of quotes, add a closing quote
  if (quotes % 2 !== 0) {
    completedJson += '"';
  }

  // Add missing closing brackets for arrays
  if (openBrackets > closeBrackets) {
    completedJson += ']'.repeat(openBrackets - closeBrackets);
  }

  // Add missing closing braces for objects
  if (openBraces > closeBraces) {
    completedJson += '}'.repeat(openBraces - closeBraces);
  }

  // Try to fix common JSON syntax errors

  // Fix trailing commas in objects
  completedJson = completedJson.replace(/,\s*}/g, '}');

  // Fix trailing commas in arrays
  completedJson = completedJson.replace(/,\s*\]/g, ']');

  // Fix missing commas between object properties
  completedJson = completedJson.replace(/"\s*{/g, '",{');

  // Fix missing commas between array elements
  completedJson = completedJson.replace(/"\s*\[/g, '",[');

  // Fix missing colons in object properties
  completedJson = completedJson.replace(/"([^"]+)"\s*"([^"]+)"/g, '"$1":"$2"');
  return completedJson;
};

// Function to extract valid JSON from a potentially broken string
const extractValidJson = jsonStr => {
  // Try to find the largest valid JSON object in the string
  let startIndex = jsonStr.indexOf('{');
  if (startIndex === -1) return null;

  // Find all possible ending positions
  let possibleEnds = [];
  let searchIndex = startIndex;
  while (true) {
    let endIndex = jsonStr.indexOf('}', searchIndex);
    if (endIndex === -1) break;
    possibleEnds.push(endIndex);
    searchIndex = endIndex + 1;
  }

  // Try each possible ending to find valid JSON
  for (let i = possibleEnds.length - 1; i >= 0; i--) {
    let endIndex = possibleEnds[i];
    let potentialJson = jsonStr.substring(startIndex, endIndex + 1);
    try {
      // Try to parse it directly
      JSON.parse(potentialJson);
      return potentialJson;
    } catch (e) {
      // If direct parsing fails, try to complete it
      try {
        let completedJson = attemptToCompleteJson(potentialJson);
        JSON.parse(completedJson);
        return completedJson;
      } catch (e2) {
        // Continue to the next possible ending
      }
    }
  }
  return null;
};

/**
 * Build the streaming-synopsis URL for the given product/pipeline pair.
 *
 *   FDA product → /products/public-product-synopsis/:productID/:type
 *   Pipeline    → /pipeline-drugs/synopsis/:slug
 *
 * The pipeline endpoint is wired to mirror the product one exactly
 * (same SSE chunks, same JSON schema, same cache-after-first-stream
 * behavior) so the rest of this hook stays unchanged.
 */
const buildSynopsisUrl = (productId, productType) => {
  if ((productType || '').toLowerCase() === 'pipeline') {
    return `${process.env.NEXT_PUBLIC_BASE_URL}/pipeline-drugs/synopsis/${productId}?stream=true`;
  }
  return `${process.env.NEXT_PUBLIC_BASE_URL}/products/public-product-synopsis/${productId}/${productType}?stream=true`;
};

// Function to generate product synopsis using streaming API with EventSource
const generateProductSynopsis = async (productId, productType, onChunkReceived) => {
  return new Promise((resolve, reject) => {
    // Initialize empty schema-based object
    const emptySchema = {
      _sourceStatus: '',
      category: '',
      indication: [],
      mechanism: '',
      efficacy: '',
      safety: '',
      dosing: '',
      patient: '',
      cost: '',
      reviews: {
        rating: 0,
        comments: ''
      }
    };

    // Initialize with empty schema
    let jsonData = {
      ...emptySchema
    };

    // Call onChunkReceived with initial empty data
    if (onChunkReceived) {
      onChunkReceived(jsonData);
    }

    // Create EventSource for streaming. URL switches based on
    // whether this is an FDA product or a pipeline drug (the BE
    // exposes the same SSE contract on both endpoints).
    const eventSource = new EventSource(buildSynopsisUrl(productId, productType));
    let accumulatedJson = '';

    // Handle incoming messages
    eventSource.onmessage = event => {
      const jsonStr = event.data;
      if (jsonStr === '[DONE]') {
        eventSource.close();
        resolve(jsonData);
        return;
      }

      // Accumulate JSON content
      accumulatedJson += jsonStr;
      try {
        // First try to extract valid JSON from the accumulated string
        const validJson = extractValidJson(accumulatedJson);

        // If we found valid JSON, try to parse it
        if (validJson) {
          try {
            // Try direct parsing first
            const directParsed = JSON.parse(validJson);

            // Update jsonData with the parsed object
            Object.keys(emptySchema).forEach(key => {
              if (directParsed[key] !== undefined) {
                jsonData[key] = directParsed[key];
              }
            });

            // Call the callback with the updated data
            if (onChunkReceived) {
              onChunkReceived({
                ...jsonData
              });
            }
          } catch (e) {
            // Direct parsing failed, trying alternative method
          }
        }

        // If direct parsing failed or no valid JSON was found, try the alternative method
        try {
          const parsedJson = JSON.parse(attemptToCompleteJson(accumulatedJson));

          // Update jsonData with any new fields from parsedJson
          if (parsedJson) {
            if (parsedJson._sourceStatus !== undefined) {
              jsonData._sourceStatus = parsedJson._sourceStatus;
            }

            // Handle each field in the schema
            if (parsedJson.category !== undefined) {
              jsonData.category = parsedJson.category;
            }
            if (parsedJson.indication !== undefined) {
              jsonData.indication = parsedJson.indication;
            }
            if (parsedJson.mechanism !== undefined) {
              jsonData.mechanism = parsedJson.mechanism;
            }
            if (parsedJson.efficacy !== undefined) {
              jsonData.efficacy = parsedJson.efficacy;
            }
            if (parsedJson.safety !== undefined) {
              jsonData.safety = parsedJson.safety;
            }
            if (parsedJson.dosing !== undefined) {
              jsonData.dosing = parsedJson.dosing;
            }
            if (parsedJson.patient !== undefined) {
              jsonData.patient = parsedJson.patient;
            }
            if (parsedJson.cost !== undefined) {
              jsonData.cost = parsedJson.cost;
            }
            if (parsedJson.reviews !== undefined) {
              jsonData.reviews = {
                ...jsonData.reviews,
                ...parsedJson.reviews
              };
            }

            // Call the callback with the updated data
            if (onChunkReceived) {
              onChunkReceived({
                ...jsonData
              });
            }
          }
        } catch (e) {
          // Silent error handling for parsing attempts
        }
      } catch (e) {
        console.error('Error processing chunk:', e);
      }
    };

    // Handle connection errors
    eventSource.onerror = error => {
      console.error('EventSource error:', error);
      eventSource.close();
      reject(error);
    };

    // Handle connection open
    eventSource.onopen = () => {
      console.log('EventSource connection opened');
    };
  });
};
const useSynopsisQuery = () => {
  const [productId, setProductId] = useState();
  const [productType, setproductType] = useState();
  const [streamingSynopsis, setStreamingSynopsis] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  useEffect(() => {
    if (productId && productType) {
      startStreamingSynopsis(productId, productType);
    }
  }, [productId, productType]);

  // Function to start streaming synopsis generation
  const startStreamingSynopsis = async (productId_0, productType_0) => {
    setIsStreaming(true);
    setStreamingSynopsis(null);
    try {
      await generateProductSynopsis(productId_0, productType_0, chunkData => {
        setStreamingSynopsis(chunkData);
      });
    } catch (error) {
      console.error('Error generating streaming synopsis:', error);
    } finally {
      setIsStreaming(false);
    }
  };
  return {
    setproductType,
    setProductId,
    streamingSynopsis,
    isStreaming,
    startStreamingSynopsis
  };
};
export default useSynopsisQuery;